/**
 * TRIAGEM POR ÁREA — perguntas feitas na janela de agendamento e lista de documentos.
 * Para mudar uma pergunta ou um documento, edite apenas este arquivo.
 *  - type "choice": o cliente escolhe uma das opções
 *  - type "text":   resposta curta digitada
 *  - short: rótulo curto usado na mensagem do WhatsApp
 *  - showIf: { id, equals } → a pergunta só aparece se outra resposta for igual a "equals"
 *  - hideIf: { id, equals } → a pergunta some se outra resposta for igual a "equals"
 *  - notices: { "opção": "texto" } → aviso destacado quando o cliente escolhe aquela opção
 *  - documents: documentos pedidos sempre naquela área
 *  - docRules: documentos extras conforme a resposta de uma pergunta ({ id, map: { "opção": [...] } })
 *  - urgentIf: { "opção": "motivo" } → marca a mensagem como URGENTE com o motivo indicado
 */

const MARIA_DA_PENHA = "Lei Maria da Penha / medida protetiva";
const TRABALHADOR = "Trabalhador(a) / empregado(a)";
const EMPREGADOR = "Empregador(a) / empresa";

/** Perguntas feitas em TODAS as áreas (aparecem depois das perguntas específicas). */
export const COMMON_QUESTIONS = [
  { id: "cidade_cliente", short: "Cidade", label: "Cidade onde você mora", type: "text", placeholder: "Ex.: Campestre/MG" },
  {
    id: "unidade", short: "Unidade",
    label: "Qual unidade fica melhor para você?",
    type: "choice",
    options: ["Campestre", "Poços de Caldas", "Atendimento online"],
  },
  {
    id: "cliente", short: "Já é cliente",
    label: "Você já é cliente do escritório?",
    type: "choice",
    options: ["Não, é um caso novo", "Sim, e tenho um caso novo", "Sim, quero saber do meu processo"],
  },
  {
    id: "processo", short: "Processo",
    label: "Número do processo ou nome do titular da ação",
    type: "text",
    placeholder: "Ex.: 5001234-56.2025.8.13.0110",
    showIf: { id: "cliente", equals: "Sim, quero saber do meu processo" },
  },
  {
    id: "prazo", short: "Intimação/prazo",
    label: "Recebeu alguma intimação, citação, carta ou prazo sobre o caso?",
    type: "choice",
    options: ["Não", "Sim"],
    urgentIf: { Sim: "Recebi intimação/prazo sobre o caso." },
  },
];

/** Como cada resposta de "já é cliente" aparece na 1ª linha da mensagem do WhatsApp. */
export const CLIENT_STATUS = {
  "Não, é um caso novo": "🆕 Caso novo",
  "Sim, e tenho um caso novo": "📂 Já é cliente · 🆕 Caso novo",
  "Sim, quero saber do meu processo": "📂 Já é cliente · 🔎 Andamento do processo",
};

/** Resposta que marca a conversa como URGENTE na mensagem do WhatsApp. */
export const URGENT = { id: "prazo", equals: "Sim" };

export const TRIAGE = {
  previdenciario: {
    questions: [
      {
        id: "tipo", short: "Assunto",
        label: "O que você precisa?",
        type: "choice",
        options: [
          "Pedir aposentadoria ou benefício",
          "Benefício negado ou cortado",
          "Revisão de benefício",
          "BPC/LOAS (idoso ou pessoa com deficiência)",
          "Pensão por morte",
          "Auxílio-doença / incapacidade",
        ],
      },
      { id: "idade", short: "Idade", label: "Sua idade", type: "text", placeholder: "Ex.: 58 anos" },
      {
        id: "rural", short: "Trabalho rural",
        label: "Já trabalhou na roça (atividade rural)?",
        type: "choice",
        options: ["Não", "Sim, parte da vida", "Sim, a maior parte da vida"],
      },
      {
        id: "vinculos", short: "Outros vínculos",
        label: "Também trabalhou como:",
        type: "choice",
        options: ["Carteira assinada", "Autônomo / MEI", "Os dois", "Nenhum"],
      },
      {
        id: "inss", short: "Pedido no INSS",
        label: "Já fez algum pedido no INSS sobre isso?",
        type: "choice",
        options: ["Ainda não", "Sim, foi negado", "Sim, está em análise", "Já recebo benefício"],
      },
      {
        id: "meuinss", short: "Acesso ao Meu INSS",
        label: "Tem acesso ao Meu INSS (senha gov.br)?",
        type: "choice",
        options: ["Sim", "Não", "Não sei"],
      },
    ],
    documents: [
      "RG e CPF",
      "Comprovante de residência atualizado",
    ],
    docRules: [
      {
        id: "tipo",
        map: {
          "Pedir aposentadoria ou benefício": [
            "Todas as Carteiras de Trabalho que tiver",
            "Extrato CNIS (tiramos juntos pelo Meu INSS, se precisar)",
            "Carnês ou guias de contribuição, se pagou como autônomo/MEI",
          ],
          "Benefício negado ou cortado": [
            "Carta do INSS com a negativa ou o corte",
            "Número do benefício (NB) ou do protocolo do pedido",
            "Extrato CNIS (tiramos juntos pelo Meu INSS, se precisar)",
          ],
          "Revisão de benefício": [
            "Carta de concessão do benefício",
            "Extrato de pagamento do benefício (Meu INSS)",
            "Todas as Carteiras de Trabalho que tiver",
          ],
          "BPC/LOAS (idoso ou pessoa com deficiência)": [
            "Cadastro Único (CadÚnico) atualizado",
            "RG e CPF de todos que moram na casa",
            "Comprovantes de renda de quem mora na casa",
            "Laudos e exames médicos, se for pedido por deficiência",
            "Carta do INSS, se o pedido foi negado",
          ],
          "Pensão por morte": [
            "Certidão de óbito",
            "Certidão de casamento ou provas da união estável",
            "Documentos de quem faleceu: RG, CPF e Carteiras de Trabalho ou carta de aposentadoria",
          ],
          "Auxílio-doença / incapacidade": [
            "Laudos, exames e receitas médicas recentes",
            "Atestados de afastamento do trabalho",
            "Carta do INSS, se o pedido foi negado ou cortado",
          ],
        },
      },
      {
        id: "rural",
        map: {
          "Sim, parte da vida": [
            "Provas do trabalho rural: notas de produtor, ITR, contratos de parceria, declaração do sindicato",
          ],
          "Sim, a maior parte da vida": [
            "Provas do trabalho rural: notas de produtor, ITR, contratos de parceria, declaração do sindicato",
          ],
        },
      },
    ],
  },

  consumidor: {
    questions: [
      {
        id: "tipo", short: "Assunto",
        label: "O que está acontecendo?",
        type: "choice",
        options: [
          "Empréstimo descontado que não reconheço",
          "Cartão consignado (RMC/RCC) que não pedi",
          "Benefício transferido de banco sem autorização",
          "Cobrança ou negativação indevida",
          "Compra, serviço ou viagem",
          "Outro",
        ],
      },
      {
        id: "desconto", short: "Desconto em",
        label: "Se há desconto, ele é feito em:",
        type: "choice",
        options: ["Benefício do INSS", "Salário", "Conta corrente", "Não há desconto"],
      },
      {
        id: "titular", short: "Titular menor/PcD",
        label: "O titular do benefício é menor de idade ou pessoa com deficiência?",
        type: "choice",
        options: ["Sim", "Não", "Não se aplica"],
      },
      { id: "empresa", short: "Empresa/banco", label: "Qual empresa ou banco está envolvido?", type: "text", placeholder: "Ex.: Banco X, loja Y, companhia aérea Z" },
      { id: "quando", short: "Quando", label: "Quando aconteceu?", type: "text", placeholder: "Ex.: março de 2026" },
      {
        id: "reclamou", short: "Já reclamou",
        label: "Já reclamou com a empresa ou no Procon / consumidor.gov.br?",
        type: "choice",
        options: ["Não", "Sim, sem solução", "Sim, ainda aguardando"],
      },
      {
        id: "provas", short: "Comprovantes",
        label: "Tem comprovantes (notas, contratos, prints, protocolos)?",
        type: "choice",
        options: ["Sim", "Alguns", "Não"],
      },
    ],
    documents: [
      "RG e CPF",
      "Comprovante de residência",
    ],
    docRules: [
      {
        id: "tipo",
        map: {
          "Empréstimo descontado que não reconheço": [
            "Extrato de empréstimos consignados (Meu INSS)",
            "Extrato do benefício ou contracheque mostrando os descontos",
            "Extrato bancário do período, se o dinheiro caiu na sua conta",
          ],
          "Cartão consignado (RMC/RCC) que não pedi": [
            "Extrato de empréstimos consignados (Meu INSS)",
            "Extrato do benefício ou contracheque mostrando os descontos",
            "Faturas do cartão, se chegaram para você",
          ],
          "Benefício transferido de banco sem autorização": [
            "Extrato do benefício (Meu INSS) mostrando o banco atual",
            "Extratos da conta antiga e da conta nova",
            "Protocolos de reclamação no banco ou no INSS, se houver",
          ],
          "Cobrança ou negativação indevida": [
            "Consulta do Serasa ou SPC",
            "Cartas, mensagens ou faturas de cobrança",
            "Comprovantes de pagamento, se você já pagou",
          ],
          "Compra, serviço ou viagem": [
            "Nota fiscal, contrato ou comprovante da compra/reserva",
            "Prints de conversas e e-mails com a empresa",
            "Números de protocolo de atendimento",
          ],
          "Outro": [
            "Documentos e comprovantes ligados ao problema",
          ],
        },
      },
    ],
  },

  familia: {
    questions: [
      {
        id: "tipo", short: "Assunto",
        label: "Qual é a situação?",
        type: "choice",
        options: [
          "Divórcio ou fim de união estável",
          "Pensão alimentícia",
          "Guarda / convivência com filhos",
          "Inventário / partilha",
          "Curatela ou interdição",
          MARIA_DA_PENHA,
          "Outro",
        ],
        notices: {
          [MARIA_DA_PENHA]:
            "🔒 Se alguém puder ver o seu celular, você pode enviar apenas seu nome e pedir contato, sem detalhes — e apagar a conversa depois.",
        },
      },
      {
        id: "seguranca", short: "Está em segurança agora",
        label: "Você está em segurança agora?",
        type: "choice",
        options: ["Sim", "Não"],
        showIf: { id: "tipo", equals: MARIA_DA_PENHA },
        notices: {
          Não: "🚨 Em perigo imediato, ligue 190 (Polícia Militar). Central de Atendimento à Mulher: 180 — 24h, gratuito e sigiloso.",
        },
        urgentIf: { Não: "Não estou em segurança no momento." },
      },
      {
        id: "medida", short: "Medida protetiva",
        label: "Já existe medida protetiva?",
        type: "choice",
        options: ["Sim, em vigor", "Pedida, aguardando", "Não"],
        showIf: { id: "tipo", equals: MARIA_DA_PENHA },
      },
      {
        id: "bo", short: "Boletim de ocorrência",
        label: "Já registrou boletim de ocorrência?",
        type: "choice",
        options: ["Sim", "Não", "Prefiro falar pessoalmente"],
        showIf: { id: "tipo", equals: MARIA_DA_PENHA },
      },
      {
        id: "filhos", short: "Filhos menores",
        label: "Há filhos menores de idade envolvidos?",
        type: "choice",
        options: ["Sim", "Não"],
      },
      {
        id: "acordo", short: "Acordo entre as partes",
        label: "Existe acordo entre as partes?",
        type: "choice",
        options: ["Sim, estamos de acordo", "Em parte", "Não", "Não sei"],
        hideIf: { id: "tipo", equals: MARIA_DA_PENHA },
      },
      {
        id: "obito", short: "Falecimento",
        label: "Em inventário: quando foi o falecimento?",
        type: "text",
        placeholder: "Ex.: junho de 2024",
        showIf: { id: "tipo", equals: "Inventário / partilha" },
      },
    ],
    documents: [
      "RG e CPF",
      "Comprovante de residência",
    ],
    docRules: [
      {
        id: "tipo",
        map: {
          "Divórcio ou fim de união estável": [
            "Certidão de casamento ou provas da união estável",
            "Certidão de nascimento dos filhos menores, se houver",
            "Documentos dos bens: escrituras, matrículas, documentos de veículos",
          ],
          "Pensão alimentícia": [
            "Certidão de nascimento dos filhos",
            "Comprovantes de renda (seus e, se tiver, da outra parte)",
            "Comprovantes de gastos dos filhos: escola, saúde, alimentação",
            "Decisão ou acordo anterior, se for revisão ou cobrança",
          ],
          "Guarda / convivência com filhos": [
            "Certidão de nascimento dos filhos",
            "Decisão ou acordo anterior sobre guarda, se houver",
          ],
          "Inventário / partilha": [
            "Certidão de óbito",
            "Certidão de casamento de quem faleceu, se era casado(a)",
            "RG, CPF e certidões dos herdeiros",
            "Documentos dos bens: escrituras, matrículas, veículos, extratos",
            "Testamento, se houver",
          ],
          "Curatela ou interdição": [
            "RG e CPF da pessoa a ser curatelada",
            "Relatório médico sobre a condição da pessoa",
            "Certidão que comprove o seu parentesco com ela",
          ],
          [MARIA_DA_PENHA]: [
            "Boletim de ocorrência e decisão de medida protetiva, se houver",
            "Prints, fotos, áudios ou outras provas que você tiver guardadas com segurança",
          ],
          "Outro": [
            "Documentos ligados à situação",
          ],
        },
      },
    ],
  },

  trabalhista: {
    questions: [
      {
        id: "perfil", short: "Perfil",
        label: "Você é:",
        type: "choice",
        options: [TRABALHADOR, EMPREGADOR],
      },
      // ——— Trabalhador(a)
      {
        id: "situacao", short: "Situação na empresa",
        label: "Qual a sua situação na empresa?",
        type: "choice",
        options: ["Ainda trabalho lá", "Fui demitido(a)", "Pedi demissão", "Outra"],
        hideIf: { id: "perfil", equals: EMPREGADOR },
      },
      { id: "saida", short: "Data da saída", label: "Se saiu, quando foi?", type: "text", placeholder: "Ex.: janeiro de 2026", hideIf: { id: "perfil", equals: EMPREGADOR } },
      {
        id: "carteira", short: "Carteira assinada",
        label: "Tinha carteira assinada?",
        type: "choice",
        options: ["Sim", "Não", "Só parte do tempo"],
        hideIf: { id: "perfil", equals: EMPREGADOR },
      },
      // ——— Empregador(a) / empresa
      {
        id: "necessidade", short: "Necessidade da empresa",
        label: "Do que a empresa precisa?",
        type: "choice",
        options: [
          "Defesa em ação trabalhista",
          "Demissão / rescisão de funcionário",
          "Contratos e admissão",
          "Orientação preventiva / rotinas",
          "Outra",
        ],
        showIf: { id: "perfil", equals: EMPREGADOR },
      },
      {
        id: "funcionarios", short: "Nº de funcionários",
        label: "Quantos funcionários a empresa tem?",
        type: "choice",
        options: ["1 a 5", "6 a 20", "Mais de 20"],
        showIf: { id: "perfil", equals: EMPREGADOR },
      },
      { id: "ramo", short: "Ramo da empresa", label: "Ramo de atividade da empresa", type: "text", placeholder: "Ex.: comércio, cafeicultura, restaurante", showIf: { id: "perfil", equals: EMPREGADOR } },
    ],
    documents: ["RG e CPF do responsável"],
    docRules: [
      {
        id: "perfil",
        map: {
          [TRABALHADOR]: ["Carteira de Trabalho"],
          [EMPREGADOR]: ["Cartão CNPJ e contrato social da empresa"],
        },
      },
      {
        id: "situacao",
        map: {
          "Ainda trabalho lá": [
            "Holerites (contracheques)",
            "Cartões de ponto, mensagens ou e-mails sobre horários e ordens",
          ],
          "Fui demitido(a)": [
            "Termo de rescisão (TRCT) e guias do seguro-desemprego",
            "Extrato do FGTS",
            "Últimos holerites (contracheques)",
          ],
          "Pedi demissão": [
            "Termo de rescisão (TRCT)",
            "Extrato do FGTS",
            "Últimos holerites (contracheques)",
          ],
          "Outra": [
            "Holerites (contracheques)",
            "Documentos ligados à situação",
          ],
        },
      },
      {
        id: "carteira",
        map: {
          "Não": [
            "Provas do trabalho: mensagens, comprovantes de pagamento, fotos e nomes de testemunhas",
          ],
          "Só parte do tempo": [
            "Provas do período sem registro: mensagens, comprovantes de pagamento e nomes de testemunhas",
          ],
        },
      },
      {
        id: "necessidade",
        map: {
          "Defesa em ação trabalhista": [
            "Citação ou notificação recebida da Justiça do Trabalho",
            "Ficha de registro, contrato e holerites do funcionário",
            "Cartões de ponto e recibos de férias e 13º",
            "Termo de rescisão (TRCT) e comprovantes de pagamento, se houver",
          ],
          "Demissão / rescisão de funcionário": [
            "Ficha de registro e contrato do funcionário",
            "Últimos holerites e cartões de ponto",
            "Advertências ou suspensões aplicadas, se houver",
          ],
          "Contratos e admissão": [
            "Dados da vaga: função, jornada e salário",
            "Modelos de contrato que a empresa já usa, se houver",
          ],
          "Orientação preventiva / rotinas": [
            "Lista de funcionários com função e jornada",
            "Documentos e rotinas que a empresa já usa (ponto, contratos, regulamento)",
          ],
          "Outra": ["Documentos ligados à situação"],
        },
      },
    ],
  },

  imobiliario: {
    questions: [
      {
        id: "papel", short: "Posição no caso",
        label: "No caso, você é:",
        type: "choice",
        options: ["Proprietário(a)", "Inquilino(a)", "Comprador(a)", "Ocupo o imóvel sem escritura"],
      },
      {
        id: "documento", short: "Documento do imóvel",
        label: "Tem contrato, escritura ou matrícula do imóvel?",
        type: "choice",
        options: ["Sim", "Só contrato particular", "Não"],
      },
      { id: "cidade", short: "Cidade do imóvel", label: "Cidade do imóvel", type: "text", placeholder: "Ex.: Poços de Caldas/MG" },
      {
        id: "acao", short: "Processo judicial",
        label: "Já existe processo judicial sobre o imóvel?",
        type: "choice",
        options: ["Não", "Sim", "Não sei"],
      },
    ],
    documents: [
      "RG e CPF",
    ],
    docRules: [
      {
        id: "papel",
        map: {
          "Proprietário(a)": [
            "Escritura e matrícula atualizada do imóvel",
            "Contrato de locação ou de venda, se houver",
            "Notificações recebidas ou enviadas",
          ],
          "Inquilino(a)": [
            "Contrato de locação",
            "Comprovantes de pagamento do aluguel",
            "Notificações recebidas e fotos do imóvel, se houver problema nele",
          ],
          "Comprador(a)": [
            "Contrato de compra e venda",
            "Comprovantes de pagamento",
            "Matrícula atualizada do imóvel",
          ],
          "Ocupo o imóvel sem escritura": [
            "Contratos particulares ou recibos de compra",
            "Carnês de IPTU e contas no seu nome",
            "Provas do tempo em que mora no imóvel: fotos, contas antigas, declarações de vizinhos",
          ],
        },
      },
    ],
  },
};
